import { useEffect, useMemo, useRef } from 'react'
import type { AlertEvent } from './alert-types'

export interface AlertThrottleOptions {
  cooldownMs: number
  onFire: (winner: AlertEvent, batch: AlertEvent[]) => void
}

export interface AlertThrottle {
  enqueue(event: AlertEvent): void
  clear(): void
  dispose(): void
}

export function createAlertThrottle(opts: AlertThrottleOptions): AlertThrottle {
  let lastFireAt = 0
  let queue: AlertEvent[] = []
  let timer: ReturnType<typeof setTimeout> | null = null

  function flush() {
    timer = null
    if (queue.length === 0) return
    const winner = queue.reduce((best, next) =>
      (next.priority ?? 5) > (best.priority ?? 5) ? next : best,
    )
    const batch = queue
    queue = []
    lastFireAt = Date.now()
    opts.onFire(winner, batch)
  }

  function enqueue(event: AlertEvent) {
    const now = Date.now()
    const elapsed = now - lastFireAt
    if (elapsed >= opts.cooldownMs && queue.length === 0) {
      lastFireAt = now
      opts.onFire(event, [event])
      return
    }
    queue.push(event)
    if (timer === null) {
      const wait = Math.max(0, opts.cooldownMs - elapsed)
      timer = setTimeout(flush, wait)
    }
  }

  return {
    enqueue,
    clear() {
      queue = []
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    },
    dispose() {
      if (timer !== null) clearTimeout(timer)
      queue = []
      timer = null
    },
  }
}

export function useAlertThrottle(opts: AlertThrottleOptions): AlertThrottle {
  const optsRef = useRef(opts)
  optsRef.current = opts
  const throttle = useMemo(
    () =>
      createAlertThrottle({
        cooldownMs: opts.cooldownMs,
        onFire: (winner, batch) => optsRef.current.onFire(winner, batch),
      }),
    [opts.cooldownMs],
  )
  useEffect(() => () => throttle.dispose(), [throttle])
  return throttle
}
